import { isBodyPartCompatible, type BodyPart } from '@sorrem/shared';
import { useMemo, type ReactNode } from 'react';
import { NO_BODY_PART_THUMBNAIL_ID, type AppearanceThumbnail } from '../../use-appearance-thumbnails';
import { OptionTile } from '../OptionTile';
import styles from './BodyPartPicker.module.scss';

/** "Facial Hair (Beard)" -> "Beard", "Hair (Long, Female)" -> "Long, Female" - strips whatever category label precedes the parenthesized style name, falling back to the raw catalog name if it doesn't follow that convention. */
function shortLabel(name: string): string {
    const match = /^[^(]+\((.+)\)$/.exec(name);
    return match ? match[1]! : name;
}

interface BodyPartPickerProps {
    slot: string;
    bodyParts: readonly BodyPart[];
    characterModelId: string;
    selectedId: string;
    onSelect: (bodyPartId: string) => void;
    /** Rendered images keyed by body part id (and `NO_BODY_PART_THUMBNAIL_ID` for "None"), from `useAppearanceThumbnails`. A tile shows a loading icon until its image arrives. */
    thumbnails: Readonly<Record<string, AppearanceThumbnail>>;
    /** Shows a "None" choice and allows `selectedId` to be `''` - for optional slots like facial hair. Required slots (e.g. hair, eyebrows) omit this so a style is always selected. */
    allowNone?: boolean;
    isDisabled?: boolean;
}

/** Draws a rendered style (or nothing before its first image arrives); see `OptionTile.renderImage`. */
function thumbnailRenderer(thumbnail: AppearanceThumbnail | undefined): (className: string | undefined) => ReactNode {
    return (className) => (thumbnail?.url ? <img className={className} src={thumbnail.url} alt="" draggable={false} /> : null);
}

/**
 * Generic body-part slot selector for character creation: a grid of
 * rendered previews of each style rather than a dropdown, since this is a
 * primary player-facing choice. Filtered to the given `slot` and to whichever
 * entries are compatible with the currently selected base body - the same
 * rule the debug tool's `AppearanceControls` already uses. Shared by the
 * hairstyle, eyebrows, and facial hair pickers rather than duplicated three
 * times, since all three are the exact same filter-and-select behavior over a
 * different catalog slot.
 */
export function BodyPartPicker({
    slot,
    bodyParts,
    characterModelId,
    selectedId,
    onSelect,
    thumbnails,
    allowNone = false,
    isDisabled = false,
}: BodyPartPickerProps): JSX.Element {
    const options = useMemo(
        () => bodyParts.filter((part) => isBodyPartCompatible(part, slot, characterModelId)),
        [bodyParts, slot, characterModelId],
    );

    return (
        <div className={styles.options}>
            {allowNone && (
                <OptionTile
                    label="None"
                    renderImage={thumbnailRenderer(thumbnails[NO_BODY_PART_THUMBNAIL_ID])}
                    isLoading={thumbnails[NO_BODY_PART_THUMBNAIL_ID]?.isLoading ?? true}
                    isSelected={selectedId === ''}
                    isDisabled={isDisabled}
                    onClick={() => onSelect('')}
                />
            )}
            {options.map((option) => (
                <OptionTile
                    key={option.id}
                    label={shortLabel(option.name)}
                    renderImage={thumbnailRenderer(thumbnails[option.id])}
                    isLoading={thumbnails[option.id]?.isLoading ?? true}
                    isSelected={option.id === selectedId}
                    isDisabled={isDisabled}
                    onClick={() => onSelect(option.id)}
                />
            ))}
        </div>
    );
}
